import { useState } from "react";
import { Trophy, Users, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ParticipantList from "./ParticipantList";
import { useLanguage } from "@/contexts/LanguageContext";

interface PendingBracketViewProps {
  title?: string;
  sport?: string;
  gameType?: 'singles' | 'doubles';
  estimatedParticipants?: number;
  className?: string;
  showParticipants?: boolean;
  eventType?: 'tournament' | 'league';
  eventTitle?: string;
}

const PendingBracketView = ({ 
  title,
  sport = "padel",
  gameType = 'doubles',
  estimatedParticipants = 16,
  className = "",
  showParticipants = true,
  eventType = 'tournament',
  eventTitle = ''
}: PendingBracketViewProps) => {
  const [showParticipantList, setShowParticipantList] = useState(false);
  const { t } = useLanguage();

  const displayTitle = title || t('tournament.eliminationBracket');

  if (showParticipantList) {
    return (
      <div className="space-y-4">
        <Button 
          variant="outline" 
          onClick={() => setShowParticipantList(false)}
          className="mb-4"
        >
          {t('tournament.back')}
        </Button>
        <ParticipantList 
          gameType={gameType}
          eventType={eventType}
          eventTitle={eventTitle}
        />
      </div>
    );
  }
  
  const getRounds = () => {
    if (estimatedParticipants <= 4) return [t('tournament.semifinales'), t('tournament.final')];
    if (estimatedParticipants <= 8) return [t('tournament.cuartos'), t('tournament.semifinales'), t('tournament.final')];
    if (estimatedParticipants <= 16) return [t('tournament.octavos'), t('tournament.cuartos'), t('tournament.semifinales'), t('tournament.final')];
    return [t('tournament.octavos'), t('tournament.cuartos'), t('tournament.semifinales'), t('tournament.final')];
  };

  const rounds = getRounds();

  const generatePendingMatches = (roundName: string) => {
    let matchCount = 1;
    if (roundName === t('tournament.final')) matchCount = 1;
    else if (roundName === t('tournament.semifinales')) matchCount = 2;
    else if (roundName === t('tournament.cuartos')) matchCount = 4;
    else if (roundName === t('tournament.octavos')) matchCount = 8;

    return Array.from({ length: matchCount }, (_, index) => (
      <div key={index} className="bg-white/50 backdrop-blur-sm border-2 border-dashed border-slate-300 rounded-lg p-4 min-h-[120px] flex flex-col justify-center">
        <div className="space-y-2">
          <div className="text-center">
            <div className="w-full h-3 bg-slate-200 rounded animate-pulse mb-2"></div>
            <div className="text-xs text-slate-400 font-medium">VS</div>
            <div className="w-full h-3 bg-slate-200 rounded animate-pulse mt-2"></div>
          </div>
          <div className="text-center">
            <Badge variant="outline" className="text-xs text-slate-400 border-slate-300">
              TBD
            </Badge>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <Card className={`bg-gradient-to-br from-slate-50 to-purple-50 border-slate-200 ${className}`}>
      <CardHeader>
        <CardTitle className="text-center">
          <div className="flex flex-col items-center space-y-2">
            <div className="flex items-center gap-2 text-slate-700">
              <Trophy className="h-6 w-6 text-slate-400" />
              <span className="text-2xl font-bold">{displayTitle}</span>
            </div>
            <Badge 
              variant="outline" 
              className="border-purple-300 text-purple-700 bg-purple-50 px-3 py-1"
            >
              {t('tournament.participantsToBeDecided')}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center space-y-2">
          <div className="flex justify-center items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>~{estimatedParticipants} {t('tournament.participants').toLowerCase()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{t('tournament.datesToBeConfirmed')}</span>
            </div>
          </div>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {t('tournament.bracketGeneratedOnConfirm')}
          </p>
        </div>

        <div className="space-y-6">
          {rounds.map((round) => (
            <div key={round} className="space-y-3">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-700">{round}</h3>
              </div>
              <div className={`grid gap-4 ${
                round === t('tournament.final') ? 'grid-cols-1 max-w-sm mx-auto' :
                round === t('tournament.semifinales') ? 'grid-cols-1 sm:grid-cols-2' :
                'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
              }`}>
                {generatePendingMatches(round)}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center pt-4 border-t border-slate-200 space-y-3">
          <p className="text-xs text-slate-500">
            {t('tournament.matchSchedulesPublished')}
          </p>
          {showParticipants && (
            <Button 
              variant="outline"
              onClick={() => setShowParticipantList(true)}
              className="text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              <Users className="h-4 w-4 mr-2" />
              {t('tournament.viewParticipants')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingBracketView;